import { useEffect, useState } from "react";
import axios from "axios";
import { Check, Download, Inbox, LogOut, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = "mkb_admin_token";

const fmt = (iso) => new Date(iso).toLocaleString("nl-BE", { dateStyle: "medium", timeStyle: "short" });

export default function AdminMessages() {
  const [token, setToken] = useState(() => sessionStorage.getItem(KEY) || "");
  const [input, setInput] = useState("");
  const [data, setData] = useState(null);
  const [subs, setSubs] = useState(null);
  const [tab, setTab] = useState("messages");
  const [error, setError] = useState("");

  const load = async (tk) => {
    try {
      const [r, n] = await Promise.all([
        axios.get(`${API}/contact`, { params: { token: tk } }),
        axios.get(`${API}/newsletter`, { params: { token: tk } }),
      ]);
      setData(r.data);
      setSubs(n.data);
      setError("");
      sessionStorage.setItem(KEY, tk);
      setToken(tk);
    } catch {
      setError("Ongeldige toegangscode.");
      sessionStorage.removeItem(KEY);
      setToken("");
      setData(null);
    }
  };

  useEffect(() => { if (token) load(token); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const markRead = async (id) => {
    await axios.patch(`${API}/contact/${id}/read`, null, { params: { token } });
    setData((d) => ({ ...d, unread: d.unread - 1, messages: d.messages.map((m) => (m.id === id ? { ...m, read: true } : m)) }));
  };

  const logout = () => { sessionStorage.removeItem(KEY); setToken(""); setData(null); setSubs(null); };

  const downloadCsv = () => {
    const rows = [["email", "taal", "ingeschreven_op"], ...subs.subscribers.map((s) => [s.email, s.lang, s.created_at])];
    const blob = new Blob([rows.map((r) => r.join(";")).join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "nieuwsbrief-inschrijvingen.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (!data) {
    return (
      <main className="mx-auto flex min-h-screen max-w-sm flex-col justify-center px-4" data-testid="admin-login">
        <img src="/images/logo-dark.png" alt="MKB Performance" className="mb-8 h-10 w-auto self-start" />
        <h1 className="font-display text-3xl">Berichten</h1>
        <p className="mt-1 text-sm text-[var(--muted)]">Voer je toegangscode in om contactberichten te bekijken.</p>
        <form onSubmit={(e) => { e.preventDefault(); load(input.trim()); }} className="mt-5 flex gap-2">
          <input type="password" value={input} onChange={(e) => setInput(e.target.value)} className="field flex-1 px-3 py-2.5 text-sm" placeholder="Toegangscode" data-testid="admin-token-input" />
          <button type="submit" className="btn-primary px-5 py-2.5 text-sm" data-testid="admin-login-button">Open</button>
        </form>
        {error && <p className="mt-3 text-sm text-red-600" data-testid="admin-error">{error}</p>}
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-4xl px-4 py-10 sm:px-6" data-testid="admin-messages">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <img src="/images/logo-dark.png" alt="MKB Performance" className="h-9 w-auto" />
          <div>
            <h1 className="font-display text-3xl">Berichten</h1>
            <p className="text-sm text-[var(--muted)]" data-testid="admin-count">{data.count} berichten · {data.unread} ongelezen · {subs?.count ?? 0} inschrijvingen</p>
          </div>
        </div>
        <div className="flex gap-2">
          <a href="/" className="btn-outline px-4 py-2 text-sm">Naar site</a>
          <button onClick={logout} className="btn-outline flex items-center gap-1.5 px-4 py-2 text-sm" data-testid="admin-logout"><LogOut className="h-4 w-4" /> Uitloggen</button>
        </div>
      </div>

      <div className="mb-6 flex gap-2 border-b border-[var(--line)]" data-testid="admin-tabs">
        {[["messages", `Berichten (${data.count})`], ["newsletter", `Nieuwsbrief (${subs?.count ?? 0})`]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} className={`-mb-px border-b-2 px-4 py-2 text-sm font-semibold ${tab === k ? "border-[var(--ink)]" : "border-transparent text-[var(--muted)]"}`} data-testid={`admin-tab-${k}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "newsletter" ? (
        <div data-testid="admin-newsletter">
          <div className="mb-4 flex items-center justify-between">
            <p className="text-sm text-[var(--muted)]" data-testid="admin-subs-count">{subs.count} inschrijvingen</p>
            {subs.count > 0 && (
              <button onClick={downloadCsv} className="btn-outline flex items-center gap-1.5 px-4 py-2 text-sm" data-testid="admin-download-csv"><Download className="h-4 w-4" /> Download CSV</button>
            )}
          </div>
          {subs.count === 0 ? (
            <div className="card p-12 text-center text-sm text-[var(--muted)]" data-testid="admin-subs-empty">Nog geen inschrijvingen.</div>
          ) : (
            <div className="card divide-y divide-[var(--line)]">
              {subs.subscribers.map((su) => (
                <div key={su.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm" data-testid={`admin-sub-${su.id}`}>
                  <span className="font-semibold">{su.email}</span>
                  <span className="text-xs text-[var(--muted)]">{su.lang.toUpperCase()} · {fmt(su.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : data.messages.length === 0 ? (
        <div className="card flex flex-col items-center gap-2 p-12 text-center text-sm text-[var(--muted)]" data-testid="admin-empty">
          <Inbox className="h-6 w-6" /> Nog geen berichten.
        </div>
      ) : (
        <div className="space-y-3">
          {data.messages.map((m) => (
            <article key={m.id} className={`card p-5 ${m.read ? "opacity-70" : "border-l-4 border-l-[var(--accent)]"}`} data-testid={`admin-message-${m.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-bold">{m.name} <span className="ml-2 text-xs font-semibold uppercase text-[var(--muted)]">{m.lang}</span></p>
                  <a href={`mailto:${m.email}?subject=Re: je bericht aan MKB Performance`} className="flex items-center gap-1 text-sm text-[var(--muted)] hover:text-[var(--ink)]">
                    <Mail className="h-3.5 w-3.5" /> {m.email}
                  </a>
                </div>
                <div className="text-right">
                  <p className="text-xs text-[var(--muted)]">{fmt(m.created_at)}</p>
                  {!m.read && (
                    <button onClick={() => markRead(m.id)} className="mt-1 flex items-center gap-1 text-xs font-semibold underline-offset-4 hover:underline" data-testid={`admin-mark-read-${m.id}`}>
                      <Check className="h-3.5 w-3.5" /> Markeer gelezen
                    </button>
                  )}
                </div>
              </div>
              <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed">{m.message}</p>
            </article>
          ))}
        </div>
      )}
    </main>
  );
}
